/**
 * Jobs Gigs store – GET jobs/ (all gigs) once per session per page.
 * Refetch after PATCH (like) in Gigs tab.
 */

import { create } from "zustand";
import apiClient from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

export interface JobsApiJob {
  id: number;
  title: string;
  tbl: string;
  date: string;
  location: string;
  description: string;
  duration: number;
  audience: number;
  event_type: string;
  budget: string;
  is_applied: boolean;
  is_liked: boolean;
  pitched_artists: number;
  price: { original: number; discount: number; updated_price: number };
  status: boolean;
  type?: "Basic" | "Recommended" | "Express";
}

interface JobsGigsState {
  /** All results for the currently fetched `type` (used for global sort + local pagination). */
  results: JobsApiJob[];
  count: number;
  totalAppliedJobs: number;
  lastFetchedType: JobsTypeFilter | null;
  hasFetchedInSessionByType: Record<JobsTypeFilter, boolean>;
  isLoading: boolean;
  error: string | null;

  fetchGigs: (page: number, type: JobsTypeFilter) => Promise<void>;
  ensureFetched: (page: number, type: JobsTypeFilter) => Promise<void>;
  setError: (error: string | null) => void;
  reset: () => void;
}

export type JobsTypeFilter = "standard" | "express" | "all";

let inFlightByKey: Record<string, Promise<void>> = {};

const initialState = {
  results: [],
  count: 0,
  totalAppliedJobs: 0,
  lastFetchedType: null as JobsTypeFilter | null,
  hasFetchedInSessionByType: {
    standard: false,
    express: false,
    all: false,
  } as Record<JobsTypeFilter, boolean>,
  isLoading: false,
  error: null as string | null,
};

export const useJobsGigsStore = create<JobsGigsState>()((set, get) => ({
  ...initialState,

  fetchGigs: async (page: number, type: JobsTypeFilter) => {
    // We fetch ALL pages for `type` once, then paginate locally in the UI.
    // Keep `page` in the API for compatibility, but ignore it for caching key.
    const key = `${type}:all`;
    const existing = inFlightByKey[key];
    if (existing) return existing;
    const promise = (async () => {
      set({ isLoading: true, error: null });
      try {
        const all: JobsApiJob[] = [];
        let count = 0;
        let totalAppliedJobs = 0;
        let nextPage = 1;
        const MAX_PAGES_SAFETY = 200;

        while (nextPage <= MAX_PAGES_SAFETY) {
          const params = new URLSearchParams({ type });
          if (nextPage > 1) params.set("page", String(nextPage));
          const url = `${API_ENDPOINTS.AUTH.JOBS}?${params.toString()}`;
          const res = await apiClient.get<{
            success?: boolean;
            data?: {
              results?: JobsApiJob[];
              count?: number;
              total_applied_jobs?: number;
            };
          }>(url);

          const raw = res.data?.data ?? res.data;
          const data = raw as { results?: JobsApiJob[]; count?: number; total_applied_jobs?: number } | undefined;
          const pageResults = data?.results ?? [];
          if (nextPage === 1) {
            count = data?.count ?? 0;
            totalAppliedJobs = data?.total_applied_jobs ?? 0;
          }

          all.push(...pageResults);

          if (pageResults.length === 0) break;
          if (count > 0 && all.length >= count) break;
          nextPage += 1;
        }

        set({
          results: all,
          count: count || all.length,
          totalAppliedJobs,
          lastFetchedType: type,
          hasFetchedInSessionByType: {
            ...get().hasFetchedInSessionByType,
            [type]: true,
          },
          isLoading: false,
          error: null,
        });
      } catch {
        set({
          results: [],
          count: 0,
          totalAppliedJobs: 0,
          lastFetchedType: type,
          isLoading: false,
          error: "Failed to load gigs",
        });
      } finally {
        delete inFlightByKey[key];
      }
    })();
    inFlightByKey[key] = promise;
    return promise;
  },

  ensureFetched: async (page: number, type: JobsTypeFilter) => {
    const { hasFetchedInSessionByType, lastFetchedType, fetchGigs } = get();
    if (hasFetchedInSessionByType[type] && lastFetchedType === type) return;
    return fetchGigs(page, type);
  },

  setError: (error) => set({ error }),

  reset: () => {
    inFlightByKey = {};
    set(initialState);
  },
}));
